/**
 * Reports when the renderer's legacy lifecycle and the Remix state machine settle on different
 * phases. This is a read-only observer: it derives nothing the renderer does not already compute for
 * itself, and it never participates in renderer behavior.
 *
 * Classification lives in `rendererRemixParity.ts`; this session-owned hook accepts observations
 * from the renderer UI and authoritative boundaries from the lifecycle owner. Keeping retained
 * state here means closing the visible renderer cannot erase a flow that is still active.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import {
	createDivergenceSignature,
	dispatchRendererRemixParityEvent,
} from './dispatchRendererRemixParityEvent.ts';
import type {
	RendererRemixLifecycleBoundary,
	RendererRemixLifecyclePhase,
	RendererRemixTerminalPhase,
} from './rendererRemixLifecycle.ts';
import { compareRendererRemixParity, isTerminalPhase } from './rendererRemixParity.ts';

/**
 * How long a non-terminal phase pair must hold before it counts as settled.
 *
 * Legacy readiness and the machine's watcher-driven phase land on different ticks, so a healthy
 * flow briefly reports two different phases at every transition. Reporting only what survives this
 * delay keeps that ordinary skew out of the signal.
 *
 * This delay is deliberately not applied once the machine is terminal — see the comment at the
 * report site.
 */
const PARITY_SETTLE_MS = 250;

type ReportedDivergences = Readonly<{
	remixId: string;
	signatures: Set<string>;
}>;

type PendingTerminalMismatch = {
	remixId: string;
	legacyPhase: RendererRemixTerminalPhase;
	machinePhase: RendererRemixLifecyclePhase;
};

export type RendererRemixParityObservation = Readonly<{
	enabled: boolean;
	remixId: string | undefined;
	legacyPhase: RendererRemixLifecyclePhase;
	machinePhase: RendererRemixLifecyclePhase;
}>;

export type RendererRemixParityDiagnostics = Readonly<{
	recordObservation: (observation: RendererRemixParityObservation) => (() => void) | undefined;
	recordLifecycleBoundary: (boundary: RendererRemixLifecycleBoundary) => void;
	recordLegacyTerminal: (remixId: string | undefined, phase: RendererRemixTerminalPhase) => void;
}>;

export const useRendererRemixParityDiagnostics = (): RendererRemixParityDiagnostics => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const reportedRef = useRef<ReportedDivergences>();
	const pendingTerminalRef = useRef<PendingTerminalMismatch | undefined>(undefined);
	const pendingBoundaryRef = useRef<RendererRemixLifecycleBoundary | undefined>(undefined);
	const lastTrackedObservationRef = useRef<RendererRemixParityObservation | undefined>(undefined);

	const reportDivergence = useCallback(
		(
			flowRemixId: string,
			pairLegacyPhase: RendererRemixLifecyclePhase,
			pairMachinePhase: RendererRemixLifecyclePhase,
		): void => {
			const settled = compareRendererRemixParity({
				legacyPhase: pairLegacyPhase,
				machinePhase: pairMachinePhase,
				isStable: true,
			});
			if (settled.outcome !== 'divergence') {
				return;
			}

			// Each admitted flow reports a given divergence once, so a divergence that persists across
			// many renders does not become a flood of identical events.
			const previous = reportedRef.current;
			const reported: ReportedDivergences =
				previous?.remixId === flowRemixId
					? previous
					: { remixId: flowRemixId, signatures: new Set<string>() };
			reportedRef.current = reported;

			const signature = createDivergenceSignature(settled.divergence);
			if (reported.signatures.has(signature)) {
				return;
			}
			reported.signatures.add(signature);

			dispatchRendererRemixParityEvent(createAnalyticsEvent, settled.divergence);
		},
		[createAnalyticsEvent],
	);
	const reportAndClearPendingTerminalMismatch = useCallback(
		(machineTerminalPhase?: RendererRemixLifecyclePhase): void => {
			const pending = pendingTerminalRef.current;
			if (!pending) {
				return;
			}

			reportDivergence(
				pending.remixId,
				pending.legacyPhase,
				machineTerminalPhase ?? pending.machinePhase,
			);
			pendingTerminalRef.current = undefined;
		},
		[reportDivergence],
	);

	const recordLifecycleBoundary = useCallback(
		(boundary: RendererRemixLifecycleBoundary): void => {
			const pending = pendingTerminalRef.current;
			if (!pending || pending.remixId !== boundary.remixId) {
				// Renderer state is observed in a passive effect, while lifecycle finalization can happen
				// synchronously in the event callback that changes legacy state. Retain the boundary so the
				// later terminal observation can reconcile against it regardless of arrival order.
				// If no observation ever arrives, there is no phase pair to classify; emitting then would
				// fabricate parity evidence rather than report an observed divergence.
				pendingBoundaryRef.current = boundary;
				return;
			}

			if (boundary.outcome === 'terminal-applied') {
				if (pending.legacyPhase === boundary.terminalPhase) {
					pendingTerminalRef.current = undefined;
					return;
				}

				reportAndClearPendingTerminalMismatch(boundary.terminalPhase);
				return;
			}

			reportAndClearPendingTerminalMismatch();
		},
		[reportAndClearPendingTerminalMismatch],
	);

	const recordObservation = useCallback(
		({
			enabled,
			remixId,
			legacyPhase,
			machinePhase,
		}: RendererRemixParityObservation): (() => void) | undefined => {
			if (!enabled || remixId === undefined) {
				pendingTerminalRef.current = undefined;
				pendingBoundaryRef.current = undefined;
				lastTrackedObservationRef.current = undefined;
				return;
			}

			const pending = pendingTerminalRef.current;
			if (pending && pending.remixId !== remixId) {
				// A new admitted flow is a definitive boundary for the pending flow. It ended without the
				// machine ever exposing the matching terminal outcome to this observer.
				reportAndClearPendingTerminalMismatch();
			}
			if (pendingBoundaryRef.current?.remixId !== remixId) {
				pendingBoundaryRef.current = undefined;
			}
			if (lastTrackedObservationRef.current?.remixId !== remixId) {
				lastTrackedObservationRef.current = undefined;
			}

			const pendingBoundary = pendingBoundaryRef.current;
			if (pendingBoundary?.remixId === remixId && isTerminalPhase(legacyPhase)) {
				const lastTrackedMachinePhase = lastTrackedObservationRef.current?.machinePhase;
				pendingBoundaryRef.current = undefined;
				pendingTerminalRef.current = undefined;
				if (pendingBoundary.outcome === 'terminal-applied') {
					if (legacyPhase !== pendingBoundary.terminalPhase) {
						reportDivergence(remixId, legacyPhase, pendingBoundary.terminalPhase);
					}
				} else if (lastTrackedMachinePhase) {
					reportDivergence(remixId, legacyPhase, lastTrackedMachinePhase);
				}
				return;
			}

			if (machinePhase === 'inactive') {
				// `inactive` is only a render snapshot. The lifecycle owner separately records whether a
				// terminal event was applied or the flow was released unresolved, so do not infer here.
				return;
			}
			lastTrackedObservationRef.current = {
				enabled,
				remixId,
				legacyPhase,
				machinePhase,
			};

			const currentPending = pendingTerminalRef.current;
			if (currentPending?.remixId === remixId) {
				if (isTerminalPhase(machinePhase)) {
					if (currentPending.legacyPhase === machinePhase) {
						pendingTerminalRef.current = undefined;
					} else {
						reportAndClearPendingTerminalMismatch(machinePhase);
					}
					return;
				}

				if (isTerminalPhase(legacyPhase) && currentPending.legacyPhase !== legacyPhase) {
					// A terminal legacy outcome cannot legitimately change while the same machine flow is
					// live. Preserve the first contradiction before retaining the new expectation.
					reportAndClearPendingTerminalMismatch();
				} else {
					// Once legacy has reached a terminal outcome, retain that expectation for the flow.
					// Later legacy cleanup renders cannot prove convergence or erase the observation.
					currentPending.machinePhase = machinePhase;
					return;
				}
			}

			if (legacyPhase === machinePhase) {
				pendingTerminalRef.current = undefined;
				return;
			}

			const reportSettledPair = (): void => {
				reportDivergence(remixId, legacyPhase, machinePhase);
			};

			// A terminal machine phase is already final, and deferring it would lose the observation: in
			// treatment the renderer's error and abort effects are driven by the machine phase and call
			// `onClose`, which clears the modal session and unmounts this observer in the same cycle,
			// clearing any pending timer. Report now or never.
			//
			// The mirror case is deliberately not fast-pathed. During an ordinary error or abort the
			// legacy phase updates during render while the machine catches up in a later effect, so a
			// terminal legacy phase against a live machine is expected lag rather than a divergence. Those
			// pairs remain pending until the machine reaches a definitive lifecycle boundary.
			if (isTerminalPhase(machinePhase)) {
				pendingTerminalRef.current = undefined;
				reportSettledPair();
				return;
			}

			if (isTerminalPhase(legacyPhase)) {
				const previous = pendingTerminalRef.current;
				if (previous?.remixId === remixId && previous.legacyPhase === legacyPhase) {
					previous.machinePhase = machinePhase;
					return;
				}

				pendingTerminalRef.current = {
					remixId,
					legacyPhase,
					machinePhase,
				};
				return;
			}

			pendingTerminalRef.current = undefined;

			const timeout = setTimeout(reportSettledPair, PARITY_SETTLE_MS);

			return () => clearTimeout(timeout);
		},
		[reportAndClearPendingTerminalMismatch, reportDivergence],
	);

	const recordLegacyTerminal = useCallback(
		(remixId: string | undefined, phase: RendererRemixTerminalPhase): void => {
			const lastTracked = lastTrackedObservationRef.current;
			if (!remixId || lastTracked?.remixId !== remixId) {
				return;
			}

			// A terminal legacy phase never enters the delayed non-terminal branch today. Dispose
			// defensively anyway so a future branch reordering cannot leak a timer from this
			// synchronous path.
			recordObservation({ ...lastTracked, legacyPhase: phase })?.();
		},
		[recordObservation],
	);

	useEffect(
		() => () => {
			// Ending observation is not proof of divergence. The session owner has gone away, so any
			// retained pair is deliberately discarded without classification.
			pendingTerminalRef.current = undefined;
			pendingBoundaryRef.current = undefined;
			lastTrackedObservationRef.current = undefined;
		},
		[],
	);

	return useMemo(
		() => ({ recordObservation, recordLifecycleBoundary, recordLegacyTerminal }),
		[recordLegacyTerminal, recordLifecycleBoundary, recordObservation],
	);
};
